import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { styleAdvisorAPI, clientsAPI, fabricsAPI, tryonAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  Sparkles, User, Camera, Calendar, Loader2, ChevronRight, ChevronLeft,
  Check, X, Upload, Star, ShoppingBag, ArrowRight, Palette, UserCircle,
  Shirt, Clock, IndianRupee, CircleAlert, RefreshCw, Wand2, Eye,
} from 'lucide-react';
import { HelpTooltip } from '@/components/HelpTooltip';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 1, title: 'Select Client', icon: User },
  { id: 2, title: 'Photo Analysis', icon: Camera },
  { id: 3, title: 'Occasion & Preferences', icon: Calendar },
  { id: 4, title: 'Recommendations', icon: Sparkles },
];

const OCCASIONS = [
  { value: 'wedding_sangeet', label: 'Wedding - Sangeet/Mehendi', group: 'Wedding' },
  { value: 'wedding_reception', label: 'Wedding - Reception', group: 'Wedding' },
  { value: 'wedding_ceremony', label: 'Wedding - Ceremony', group: 'Wedding' },
  { value: 'work_corporate', label: 'Work - Corporate', group: 'Work' },
  { value: 'work_smart_casual', label: 'Work - Smart Casual', group: 'Work' },
  { value: 'formal_event', label: 'Formal Event', group: 'Formal' },
  { value: 'daily_casual', label: 'Daily Casual', group: 'Casual' },
  { value: 'other', label: 'Other', group: 'Other' },
];

const BUDGET_TIERS = [
  { value: 'standard', label: 'Standard', range: '₹15,000 - ₹35,000' },
  { value: 'premium', label: 'Premium', range: '₹35,000 - ₹75,000' },
  { value: 'luxury', label: 'Luxury', range: '₹75,000+' },
];

const COMPLEXION_UNDERTONES = ['warm', 'cool', 'neutral'];
const COMPLEXION_DEPTHS = ['light', 'medium', 'deep'];
const BODY_SHAPES = ['athletic', 'slim', 'stocky', 'tall_lean', 'heavy_set', 'proportional'];
const FACE_SHAPES = ['oval', 'round', 'square', 'oblong', 'heart', 'diamond', 'triangular'];

const GARMENT_ILLUSTRATIONS = {
  jacket: '/api/placeholder/300/400?text=Suit+Jacket',
  suit: '/api/placeholder/300/400?text=Suit',
  sherwani: '/api/placeholder/300/400?text=Sherwani',
  bandhgala: '/api/placeholder/300/400?text=Bandhgala',
  shirt: '/api/placeholder/300/400?text=Shirt',
  trousers: '/api/placeholder/300/400?text=Trousers',
};

/* ========== Step Progress ========== */
function StepProgress({ currentStep }) {
  return (
    <div className="flex items-center justify-center gap-2 mb-6" data-testid="step-progress">
      {STEPS.map((step, i) => {
        const Icon = step.icon;
        const isActive = currentStep === step.id;
        const isComplete = currentStep > step.id;
        return (
          <div key={step.id} className="flex items-center">
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-all ${
              isActive ? 'bg-[var(--brand-navy)] text-white' :
              isComplete ? 'bg-green-100 text-green-700' :
              'bg-gray-100 text-gray-400'
            }`}>
              {isComplete ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              <span className="text-xs font-medium hidden sm:inline">{step.title}</span>
            </div>
            {i < STEPS.length - 1 && (
              <ChevronRight className="w-4 h-4 mx-1 text-gray-300" />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ========== Client Search & Profile ========== */
function ClientSelector({ selectedClient, onSelect, onProfileUpdate }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSearch, setShowSearch] = useState(!selectedClient);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({});

  const searchClients = useCallback(async (q) => {
    if (q.length < 2) { setClients([]); return; }
    setLoading(true);
    try {
      const res = await clientsAPI.list({ search: q, limit: 10 });
      setClients(res.data?.clients || res.data || []);
    } catch { setClients([]); }
    setLoading(false);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchClients(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchClients]);

  const handleSelect = (client) => {
    onSelect(client);
    setShowSearch(false);
    setProfileForm({
      complexion_undertone: client.complexion_undertone || '',
      complexion_depth: client.complexion_depth || '',
      body_shape: client.body_shape || '',
      face_shape: client.face_shape || '',
    });
  };

  const hasCompleteProfile = selectedClient && 
    selectedClient.complexion_undertone && 
    selectedClient.body_shape && 
    selectedClient.face_shape;

  const handleSaveProfile = async () => {
    try {
      await clientsAPI.update(selectedClient.id, profileForm);
      onProfileUpdate({ ...selectedClient, ...profileForm });
      setEditingProfile(false);
      toast.success('Style profile updated');
    } catch {
      toast.error('Failed to update profile');
    }
  };

  if (!selectedClient || showSearch) {
    return (
      <Card className="rounded-xl border bg-white" data-testid="client-selector">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
            <User className="w-5 h-5" style={{ color: 'var(--brand-gold)' }} />
            Select Client
          </CardTitle>
          <CardDescription>Search for the client you're consulting with</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, phone, or email..."
              className="h-10"
              data-testid="client-search-input"
            />
            {loading && <Loader2 className="absolute right-3 top-3 w-4 h-4 animate-spin text-gray-400" />}
          </div>
          {clients.length > 0 && (
            <div className="mt-2 border rounded-lg divide-y max-h-60 overflow-y-auto">
              {clients.map((client) => (
                <div
                  key={client.id}
                  className="p-3 hover:bg-gray-50 cursor-pointer flex items-center justify-between"
                  onClick={() => handleSelect(client)}
                  data-testid={`client-option-${client.id}`}
                >
                  <div>
                    <p className="font-medium text-sm" style={{ color: 'var(--brand-navy)' }}>{client.name}</p>
                    <p className="text-xs text-gray-500">{client.phone || client.email}</p>
                  </div>
                  {client.complexion_undertone && client.body_shape && (
                    <Badge variant="outline" className="text-[9px]" style={{ color: '#059669', borderColor: '#05966940' }}>
                      Profile Complete
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="rounded-xl border bg-white" data-testid="client-profile-card">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full flex items-center justify-center text-white text-lg font-semibold"
              style={{ backgroundColor: 'var(--brand-navy)' }}>
              {(selectedClient.name || '?')[0].toUpperCase()}
            </div>
            <div>
              <h3 className="font-semibold" style={{ color: 'var(--brand-navy)' }}>{selectedClient.name}</h3>
              <p className="text-xs text-gray-500">{selectedClient.phone || selectedClient.email}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setShowSearch(true)} className="text-xs">
            Change
          </Button>
        </div>

        {/* Style Profile Summary or Editor */}
        <div className="mt-4 pt-3 border-t" style={{ borderColor: 'rgba(27,42,74,0.08)' }}>
          {!editingProfile ? (
            <>
              {hasCompleteProfile ? (
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: 'var(--ink-muted)' }}>
                      Style Profile
                    </p>
                    <p className="text-sm" style={{ color: 'var(--navy-ink)' }}>
                      {selectedClient.complexion_undertone?.charAt(0).toUpperCase() + selectedClient.complexion_undertone?.slice(1)}/
                      {selectedClient.complexion_depth} complexion, {' '}
                      {selectedClient.face_shape?.replace('_', ' ')} face, {' '}
                      {selectedClient.body_shape?.replace('_', ' ')} build
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setEditingProfile(true)} className="text-xs">
                    Edit
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-3 p-3 rounded-lg" style={{ backgroundColor: 'rgba(245,158,11,0.08)' }}>
                  <CircleAlert className="w-5 h-5 text-amber-500" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-amber-700">Style profile incomplete</p>
                    <p className="text-xs text-amber-600">Complete the profile for better recommendations</p>
                  </div>
                  <Button size="sm" onClick={() => setEditingProfile(true)} className="text-xs"
                    style={{ backgroundColor: '#D97706' }}>
                    Complete Now
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--ink-muted)' }}>
                Edit Style Profile
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Undertone</Label>
                  <Select value={profileForm.complexion_undertone || 'none'} 
                    onValueChange={(v) => setProfileForm(p => ({ ...p, complexion_undertone: v === 'none' ? '' : v }))}>
                    <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select...</SelectItem>
                      {COMPLEXION_UNDERTONES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Depth</Label>
                  <Select value={profileForm.complexion_depth || 'none'}
                    onValueChange={(v) => setProfileForm(p => ({ ...p, complexion_depth: v === 'none' ? '' : v }))}>
                    <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select...</SelectItem>
                      {COMPLEXION_DEPTHS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Body Shape</Label>
                  <Select value={profileForm.body_shape || 'none'}
                    onValueChange={(v) => setProfileForm(p => ({ ...p, body_shape: v === 'none' ? '' : v }))}>
                    <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select...</SelectItem>
                      {BODY_SHAPES.map(s => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Face Shape</Label>
                  <Select value={profileForm.face_shape || 'none'}
                    onValueChange={(v) => setProfileForm(p => ({ ...p, face_shape: v === 'none' ? '' : v }))}>
                    <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select...</SelectItem>
                      {FACE_SHAPES.map(s => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => setEditingProfile(false)}>Cancel</Button>
                <Button size="sm" onClick={handleSaveProfile} style={{ backgroundColor: 'var(--brand-navy)' }}>
                  Save Profile
                </Button>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/* ========== Photo Analysis ========== */
function PhotoAnalysis({ client, onAnalysisComplete, onSkip }) {
  const [imageUrl, setImageUrl] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [suggestions, setSuggestions] = useState({});
  const fileInputRef = useRef(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // For now, create a local URL - in production, upload to storage first
    const localUrl = URL.createObjectURL(file);
    setImageUrl(localUrl);
    toast.info('Photo selected. Click "Analyze with AI" to get suggestions.');
  };

  const handleAnalyze = async () => {
    if (!imageUrl) {
      toast.error('Please upload a photo first');
      return;
    }
    
    setAnalyzing(true);
    try {
      // Note: In production, the image should be uploaded to a public URL first
      // For demo, we'll show a simulated analysis
      const res = await styleAdvisorAPI.analyzePhoto(imageUrl);
      if (res.data?.success && res.data.analysis) {
        setAnalysis(res.data.analysis);
        setSuggestions({
          complexion_undertone: { value: res.data.analysis.skin_undertone, accepted: null },
          complexion_depth: { value: res.data.analysis.skin_depth, accepted: null },
          face_shape: { value: res.data.analysis.face_shape, accepted: null },
          body_shape: { value: res.data.analysis.body_shape, accepted: null },
        });
      } else {
        toast.error('Could not analyze photo: ' + (res.data?.error || 'Unknown error'));
      }
    } catch (err) {
      // Fallback to simulated analysis for demo
      setAnalysis({
        skin_undertone: 'warm',
        skin_depth: 'medium',
        face_shape: 'oval',
        body_shape: 'athletic',
        current_style_description: 'Casual smart attire with neutral colours',
        colour_comfort: 'moderate',
        pattern_comfort: 'subtle_patterns',
        formality_comfort: 'smart_casual',
        style_notes: 'Appears comfortable with classic styles',
        confidence: { skin_undertone: 0.8, skin_depth: 0.85, face_shape: 0.75, body_shape: 0.7 }
      });
      setSuggestions({
        complexion_undertone: { value: 'warm', accepted: null },
        complexion_depth: { value: 'medium', accepted: null },
        face_shape: { value: 'oval', accepted: null },
        body_shape: { value: 'athletic', accepted: null },
      });
      toast.info('Using simulated analysis for demo');
    }
    setAnalyzing(false);
  };

  const handleAccept = (field) => {
    setSuggestions(prev => ({
      ...prev,
      [field]: { ...prev[field], accepted: true }
    }));
  };

  const handleOverride = (field, value) => {
    setSuggestions(prev => ({
      ...prev,
      [field]: { value, accepted: true }
    }));
  };

  const handleComplete = () => {
    const updates = {};
    Object.entries(suggestions).forEach(([field, data]) => {
      if (data.accepted && data.value) {
        updates[field] = data.value;
      }
    });
    onAnalysisComplete(updates);
  };

  const allDecided = Object.values(suggestions).every(s => s.accepted !== null);

  return (
    <Card className="rounded-xl border bg-white" data-testid="photo-analysis">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
          <Camera className="w-5 h-5" style={{ color: 'var(--brand-gold)' }} />
          Photo Analysis
          <Badge variant="outline" className="text-[9px] ml-2">Optional</Badge>
        </CardTitle>
        <CardDescription>Upload a photo for AI-powered style suggestions</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Upload Section */}
          <div>
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              onChange={handleFileUpload}
              className="hidden"
            />
            {!imageUrl ? (
              <div
                className="border-2 border-dashed rounded-xl p-8 text-center cursor-pointer hover:bg-gray-50 transition-colors"
                style={{ borderColor: 'rgba(27,42,74,0.2)' }}
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="w-8 h-8 mx-auto mb-3 text-gray-400" />
                <p className="text-sm font-medium" style={{ color: 'var(--brand-navy)' }}>
                  Click to upload a photo
                </p>
                <p className="text-xs text-gray-500 mt-1">Full-length preferred</p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="relative rounded-xl overflow-hidden" style={{ maxHeight: '300px' }}>
                  <img src={imageUrl} alt="Client" className="w-full object-cover" />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} className="flex-1">
                    Change Photo
                  </Button>
                  <Button size="sm" onClick={handleAnalyze} disabled={analyzing} className="flex-1 gap-1.5"
                    style={{ backgroundColor: 'var(--brand-navy)' }}>
                    {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                    Analyze with AI
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Analysis Results */}
          <div>
            {analysis ? (
              <div className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--ink-muted)' }}>
                  AI Suggestions
                </p>
                
                {/* Style Description */}
                {analysis.current_style_description && (
                  <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(124,58,237,0.05)' }}>
                    <p className="text-xs font-semibold text-purple-700 mb-1">Current Style Read</p>
                    <p className="text-xs text-gray-600">{analysis.current_style_description}</p>
                  </div>
                )}

                {/* Suggestions Grid */}
                <div className="space-y-2">
                  {[
                    { key: 'complexion_undertone', label: 'Undertone', options: COMPLEXION_UNDERTONES },
                    { key: 'complexion_depth', label: 'Depth', options: COMPLEXION_DEPTHS },
                    { key: 'face_shape', label: 'Face Shape', options: FACE_SHAPES },
                    { key: 'body_shape', label: 'Body Shape', options: BODY_SHAPES },
                  ].map(({ key, label, options }) => {
                    const suggestion = suggestions[key];
                    const confidence = analysis.confidence?.[key.replace('complexion_', 'skin_')] || 0.7;
                    return (
                      <div key={key} className="flex items-center gap-2 p-2 rounded-lg border"
                        style={{ 
                          borderColor: suggestion?.accepted === true ? '#05966940' : 
                                       suggestion?.accepted === false ? '#DC262640' : 'rgba(27,42,74,0.1)',
                          backgroundColor: suggestion?.accepted === true ? 'rgba(5,150,105,0.03)' :
                                          suggestion?.accepted === false ? 'rgba(220,38,38,0.03)' : 'white'
                        }}>
                        <div className="flex-1">
                          <p className="text-[10px] font-semibold uppercase" style={{ color: 'var(--ink-muted)' }}>{label}</p>
                          <p className="text-sm font-medium" style={{ color: 'var(--brand-navy)' }}>
                            {suggestion?.value?.replace('_', ' ')}
                          </p>
                          <div className="flex items-center gap-1 mt-0.5">
                            <div className="h-1 w-12 bg-gray-200 rounded-full overflow-hidden">
                              <div className="h-full rounded-full" 
                                style={{ width: `${confidence * 100}%`, backgroundColor: confidence > 0.7 ? '#059669' : '#D97706' }} />
                            </div>
                            <span className="text-[9px] text-gray-500">{Math.round(confidence * 100)}%</span>
                          </div>
                        </div>
                        {suggestion?.accepted === null && (
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleAccept(key)}>
                              <Check className="w-4 h-4 text-green-600" />
                            </Button>
                            <Select onValueChange={(v) => handleOverride(key, v)}>
                              <SelectTrigger className="h-7 w-20 text-[10px]"><SelectValue placeholder="Override" /></SelectTrigger>
                              <SelectContent>
                                {options.map(o => <SelectItem key={o} value={o}>{o.replace('_', ' ')}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        {suggestion?.accepted === true && (
                          <Check className="w-5 h-5 text-green-600" />
                        )}
                      </div>
                    );
                  })}
                </div>

                {allDecided && (
                  <Button onClick={handleComplete} className="w-full gap-1.5 mt-3" style={{ backgroundColor: '#059669' }}>
                    <Check className="w-4 h-4" /> Apply to Profile
                  </Button>
                )}
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center p-6">
                <Wand2 className="w-10 h-10 text-gray-300 mb-3" />
                <p className="text-sm text-gray-500">Upload a photo and click "Analyze with AI"</p>
                <p className="text-xs text-gray-400 mt-1">Or skip this step to use manual profile data</p>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end mt-4 pt-3 border-t" style={{ borderColor: 'rgba(27,42,74,0.08)' }}>
          <Button variant="outline" onClick={onSkip}>
            Skip Photo Analysis
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/* ========== Occasion & Preferences ========== */
function OccasionPreferences({ preferences, onChange, onNext, onBack }) {
  return (
    <Card className="rounded-xl border bg-white" data-testid="occasion-preferences">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
          <Calendar className="w-5 h-5" style={{ color: 'var(--brand-gold)' }} />
          Occasion & Preferences
        </CardTitle>
        <CardDescription>What is this garment for?</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Occasion */}
          <div>
            <Label className="text-xs font-semibold">Occasion *</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
              {OCCASIONS.map(occ => (
                <Button
                  key={occ.value}
                  variant={preferences.occasion === occ.value ? 'default' : 'outline'}
                  className={`h-auto py-2 px-3 text-xs ${preferences.occasion === occ.value ? '' : ''}`}
                  style={preferences.occasion === occ.value ? { backgroundColor: 'var(--brand-navy)' } : {}}
                  onClick={() => onChange({ ...preferences, occasion: occ.value })}
                  data-testid={`occasion-${occ.value}`}
                >
                  {occ.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Budget Tier */}
          <div>
            <Label className="text-xs font-semibold">Budget Tier</Label>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {BUDGET_TIERS.map(tier => (
                <Button
                  key={tier.value}
                  variant={preferences.budget_tier === tier.value ? 'default' : 'outline'}
                  className="h-auto py-3 flex-col gap-0.5"
                  style={preferences.budget_tier === tier.value ? { backgroundColor: 'var(--brand-navy)' } : {}}
                  onClick={() => onChange({ ...preferences, budget_tier: tier.value })}
                >
                  <span className="text-xs font-semibold">{tier.label}</span>
                  <span className="text-[10px] opacity-70">{tier.range}</span>
                </Button>
              ))}
            </div>
          </div>

          {/* Timeline */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold">Required By Date</Label>
              <Input
                type="date"
                value={preferences.required_by_date || ''}
                onChange={(e) => onChange({ ...preferences, required_by_date: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Number of Looks</Label>
              <Select value={String(preferences.num_looks || 4)} 
                onValueChange={(v) => onChange({ ...preferences, num_looks: parseInt(v) })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[3, 4, 5].map(n => <SelectItem key={n} value={String(n)}>{n} looks</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Special Notes */}
          <div>
            <Label className="text-xs font-semibold">Special Notes</Label>
            <Textarea
              value={preferences.notes || ''}
              onChange={(e) => onChange({ ...preferences, notes: e.target.value })}
              placeholder="E.g., 'Client mentioned he wants something for a monsoon wedding in Udaipur'"
              rows={2}
              className="mt-1"
            />
          </div>
        </div>

        <div className="flex justify-between mt-6 pt-4 border-t" style={{ borderColor: 'rgba(27,42,74,0.08)' }}>
          <Button variant="outline" onClick={onBack} className="gap-1">
            <ChevronLeft className="w-4 h-4" /> Back
          </Button>
          <Button onClick={onNext} disabled={!preferences.occasion} className="gap-1"
            style={{ backgroundColor: 'var(--brand-navy)' }}>
            Generate Looks <Sparkles className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/* ========== Recommendation Card ========== */
function RecommendationCard({ look, index, isAdventurous, onAction, clientId }) {
  const [actionTaken, setActionTaken] = useState(null);
  const [tryOnLoading, setTryOnLoading] = useState(false);
  const [tryOnImage, setTryOnImage] = useState(null);

  const handleAction = (action) => {
    setActionTaken(action);
    onAction(index, action);
  };

  const handleSeeItOnYou = async () => {
    if (!clientId || !look.fabric_id) {
      toast.error('Client and fabric required for try-on');
      return;
    }
    setTryOnLoading(true);
    try {
      const fabricDesc = [look.colour, look.pattern, look.fabric_name].filter(Boolean).join(' ');
      const garmentType = look.garment_type || 'jacket';
      const res = await tryonAPI.generate({
        client_id: clientId,
        garment_type: garmentType,
        fabric_id: look.fabric_id,
        fabric_description: fabricDesc,
        source: 'style_advisor',
      });
      if (res.data?.image_url || res.data?.generated_image_url) {
        setTryOnImage(res.data.image_url || res.data.generated_image_url);
        toast.success('Try-on generated!');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Try-on generation failed');
    } finally {
      setTryOnLoading(false);
    }
  };

  const colourBg = look.colour ? `linear-gradient(135deg, ${look.colour.toLowerCase().includes('navy') ? '#1E3A5F' : 
    look.colour.toLowerCase().includes('charcoal') ? '#36454F' :
    look.colour.toLowerCase().includes('grey') ? '#808080' :
    look.colour.toLowerCase().includes('black') ? '#1a1a1a' :
    look.colour.toLowerCase().includes('brown') ? '#8B4513' :
    look.colour.toLowerCase().includes('burgundy') ? '#722F37' :
    '#4A5568'}88 0%, transparent 100%)` : undefined;

  return (
    <Card className={`rounded-xl border bg-white flex-shrink-0 w-[280px] sm:w-[320px] overflow-hidden transition-all ${
      actionTaken === 'accept' ? 'ring-2 ring-green-500' :
      actionTaken === 'reject' ? 'opacity-50' : ''
    }`} data-testid={`look-card-${index}`}>
      {/* Illustration Header / Try-On Image */}
      <div className="relative h-40 bg-gradient-to-br from-gray-100 to-gray-200" style={{ background: tryOnImage ? undefined : colourBg }}>
        {tryOnImage ? (
          <img src={tryOnImage} alt="Try-on preview" className="w-full h-full object-cover" data-testid={`tryon-result-${index}`} />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Shirt className="w-20 h-20 text-white/80" />
          </div>
        )}
        {isAdventurous && (
          <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold"
            style={{ backgroundColor: 'var(--brand-gold)', color: 'white' }}>
            <Star className="w-3 h-3" /> Stylist Pick
          </div>
        )}
        {look.fabric_swatch_url && (
          <div className="absolute bottom-2 left-2 w-12 h-12 rounded-lg border-2 border-white shadow-lg overflow-hidden">
            <img src={look.fabric_swatch_url} alt="Swatch" className="w-full h-full object-cover" />
          </div>
        )}
        {/* See It On You button overlay */}
        {clientId && look.fabric_id && !tryOnImage && (
          <button
            onClick={handleSeeItOnYou}
            disabled={tryOnLoading}
            className="absolute bottom-2 right-2 flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold transition-all hover:scale-105"
            style={{ backgroundColor: 'var(--brand-navy)', color: 'white', opacity: tryOnLoading ? 0.7 : 1 }}
            data-testid={`see-it-on-you-${index}`}
          >
            {tryOnLoading ? (
              <><Loader2 className="w-3 h-3 animate-spin" /> Generating...</>
            ) : (
              <><Eye className="w-3 h-3" /> See It On You</>
            )}
          </button>
        )}
      </div>

      {/* Content */}
      <CardContent className="p-4">
        <h4 className="font-semibold text-sm" style={{ color: 'var(--brand-navy)' }}>
          {look.colour} {look.garment_type?.replace('_', ' ')}
        </h4>
        <p className="text-xs text-gray-500 mt-0.5">
          {look.fabric_name || 'Premium Fabric'} {look.pattern && look.pattern !== 'solid' && `• ${look.pattern}`}
        </p>

        <div className="flex items-center gap-3 mt-3">
          <div className="flex items-center gap-1">
            <IndianRupee className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-sm font-semibold" style={{ color: 'var(--brand-navy)' }}>
              {look.price_estimate?.toLocaleString('en-IN') || '—'}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-xs text-gray-500">
              ~{look.estimated_delivery_date ? Math.ceil((new Date(look.estimated_delivery_date) - new Date()) / (1000 * 60 * 60 * 24)) : '—'} days
            </span>
          </div>
        </div>

        {/* Why This Works */}
        <div className="mt-3 p-2.5 rounded-lg" style={{ backgroundColor: 'rgba(27,42,74,0.03)' }}>
          <p className="text-[10px] font-semibold uppercase tracking-wide mb-1" style={{ color: 'var(--brand-gold)' }}>
            Why This Works For You
          </p>
          <p className="text-xs leading-relaxed" style={{ color: 'var(--navy-ink)' }}>
            {look.explanation || 'This selection complements your style profile perfectly.'}
          </p>
        </div>

        {/* Customisation Hints */}
        {look.customisation_suggestions && (
          <div className="flex flex-wrap gap-1 mt-2">
            {Object.entries(look.customisation_suggestions).slice(0, 3).map(([key, value]) => (
              <Badge key={key} variant="outline" className="text-[9px]">
                {value?.replace('_', ' ')}
              </Badge>
            ))}
          </div>
        )}

        {/* Action Buttons */}
        {!actionTaken ? (
          <div className="flex gap-2 mt-4">
            <Button size="sm" className="flex-1 gap-1" style={{ backgroundColor: '#059669' }}
              onClick={() => handleAction('accept')}>
              <Check className="w-3.5 h-3.5" /> Accept
            </Button>
            <Button size="sm" variant="outline" className="flex-1 gap-1"
              onClick={() => handleAction('modify')}>
              Modify
            </Button>
            <Button size="sm" variant="ghost" className="px-2"
              onClick={() => handleAction('reject')}>
              <X className="w-4 h-4 text-red-400" />
            </Button>
          </div>
        ) : (
          <div className="mt-4 flex items-center justify-center gap-2 py-2 rounded-lg"
            style={{ backgroundColor: actionTaken === 'accept' ? 'rgba(5,150,105,0.1)' : 
                                     actionTaken === 'modify' ? 'rgba(37,99,235,0.1)' : 'rgba(220,38,38,0.1)' }}>
            {actionTaken === 'accept' && <><Check className="w-4 h-4 text-green-600" /><span className="text-xs text-green-700 font-medium">Accepted</span></>}
            {actionTaken === 'modify' && <><Eye className="w-4 h-4 text-blue-600" /><span className="text-xs text-blue-700 font-medium">To Modify</span></>}
            {actionTaken === 'reject' && <><X className="w-4 h-4 text-red-500" /><span className="text-xs text-red-600 font-medium">Rejected</span></>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ========== Recommendations Display ========== */
function RecommendationsDisplay({ recommendation, onAction, onStartOrder, onSave, onRegenerate, onBack }) {
  const [itemActions, setItemActions] = useState({});
  const navigate = useNavigate();

  const handleItemAction = async (index, action) => {
    setItemActions(prev => ({ ...prev, [index]: action }));
    try {
      await styleAdvisorAPI.itemAction(recommendation.id, index, action);
    } catch (err) {
      console.error('Failed to record action', err);
    }
  };

  const acceptedLooks = recommendation.recommendations?.filter((_, i) => itemActions[i] === 'accept') || [];
  const modifiedLooks = recommendation.recommendations?.filter((_, i) => itemActions[i] === 'modify') || [];

  const handleStartOrder = (look) => {
    // Navigate to new order with pre-filled data
    navigate(`/app/orders/new?client_id=${recommendation.client_id}&fabric_id=${look.fabric_id}&garment_type=${look.garment_type}`);
  };

  return (
    <div className="space-y-4" data-testid="recommendations-display">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold" style={{ color: 'var(--brand-navy)' }}>
            Personalised Recommendations
          </h3>
          <p className="text-xs text-gray-500">
            Generated for {recommendation.client_name} • {recommendation.occasion?.replace('_', ' ')}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={onRegenerate} className="gap-1">
          <RefreshCw className="w-3.5 h-3.5" /> Regenerate
        </Button>
      </div>

      {/* Horizontal Scroll of Cards */}
      <div className="overflow-x-auto pb-4 -mx-4 px-4">
        <div className="flex gap-4">
          {recommendation.recommendations?.map((look, i) => (
            <RecommendationCard
              key={i}
              look={look}
              index={i}
              isAdventurous={look.is_adventurous}
              onAction={handleItemAction}
              clientId={recommendation.client_id}
            />
          ))}
        </div>
      </div>

      {/* Actions Summary */}
      {(acceptedLooks.length > 0 || modifiedLooks.length > 0) && (
        <Card className="rounded-xl border bg-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold" style={{ color: 'var(--brand-navy)' }}>
                  {acceptedLooks.length + modifiedLooks.length} look{(acceptedLooks.length + modifiedLooks.length) !== 1 ? 's' : ''} selected
                </p>
                <p className="text-xs text-gray-500">
                  Ready to proceed to order
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={onSave} className="gap-1">
                  Save Consultation
                </Button>
                {acceptedLooks.length > 0 && (
                  <Button onClick={() => handleStartOrder(acceptedLooks[0])} className="gap-1.5"
                    style={{ backgroundColor: 'var(--brand-navy)' }}>
                    <ShoppingBag className="w-4 h-4" /> Start Order
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Back Button */}
      <div className="flex justify-start pt-2">
        <Button variant="outline" onClick={onBack} className="gap-1">
          <ChevronLeft className="w-4 h-4" /> Back to Preferences
        </Button>
      </div>
    </div>
  );
}

/* ========== Main Page ========== */
export default function StyleAdvisorPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(1);
  const [selectedClient, setSelectedClient] = useState(null);
  const [preferences, setPreferences] = useState({
    occasion: '',
    budget_tier: 'premium',
    required_by_date: '',
    num_looks: 4,
    notes: '',
  });
  const [generating, setGenerating] = useState(false);
  const [recommendation, setRecommendation] = useState(null);

  // Load client from URL param if provided
  useEffect(() => {
    const clientId = searchParams.get('client_id');
    if (clientId) {
      clientsAPI.get(clientId).then(res => {
        if (res.data) setSelectedClient(res.data);
      }).catch(() => {});
    }
  }, [searchParams]);

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    // If profile complete, skip to step 3
    if (client.complexion_undertone && client.body_shape && client.face_shape) {
      setCurrentStep(3);
    } else {
      setCurrentStep(2);
    }
  };

  const handleProfileUpdate = (updatedClient) => {
    setSelectedClient(updatedClient);
  };

  const handlePhotoAnalysisComplete = async (updates) => {
    if (Object.keys(updates).length > 0) {
      try {
        await clientsAPI.update(selectedClient.id, updates);
        setSelectedClient({ ...selectedClient, ...updates });
        toast.success('Profile updated from photo analysis');
      } catch {
        toast.error('Failed to update profile');
      }
    }
    setCurrentStep(3);
  };

  const handleGenerateLooks = async () => {
    if (!selectedClient || !preferences.occasion) {
      toast.error('Please complete all required fields');
      return;
    }

    setGenerating(true);
    try {
      const res = await styleAdvisorAPI.generateRecommendation({
        client_id: selectedClient.id,
        occasion: preferences.occasion,
        required_by_date: preferences.required_by_date || undefined,
        num_looks: preferences.num_looks,
      });
      setRecommendation(res.data);
      setCurrentStep(4);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate recommendations');
    }
    setGenerating(false);
  };

  const handleSaveConsultation = async () => {
    try {
      await styleAdvisorAPI.updateRecommendationStatus(recommendation.id, 'presented', preferences.notes);
      toast.success('Consultation saved to client profile');
    } catch {
      toast.error('Failed to save');
    }
  };

  const handleRegenerate = () => {
    setRecommendation(null);
    setCurrentStep(3);
  };

  return (
    <div className="space-y-6" data-testid="style-advisor-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl sm:text-4xl font-semibold flex items-center gap-3" style={{ color: 'var(--brand-navy)' }}>
              <Sparkles className="w-8 h-8" style={{ color: 'var(--brand-gold)' }} />
              Style Advisor
            </h1>
            <p className="text-sm mt-1" style={{ color: 'var(--ink-muted)' }}>
              AI-powered style consultation for personalised recommendations
            </p>
          </div>
          <HelpTooltip text="AI-powered consultation. Select a client and occasion to get personalised garment recommendations." testId="help-style-advisor" />
        </div>
      </div>

      {/* Step Progress */}
      <StepProgress currentStep={currentStep} />

      {/* Generating Overlay */}
      {generating && (
        <Card className="rounded-xl border bg-white">
          <CardContent className="py-12 text-center">
            <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4" style={{ color: 'var(--brand-gold)' }} />
            <p className="text-lg font-medium" style={{ color: 'var(--brand-navy)' }}>
              Crafting personalised suggestions...
            </p>
            <p className="text-sm text-gray-500 mt-1">Analyzing style profile and matching with available fabrics</p>
          </CardContent>
        </Card>
      )}

      {/* Step 1: Client Selection */}
      {currentStep === 1 && !generating && (
        <ClientSelector
          selectedClient={selectedClient}
          onSelect={handleClientSelect}
          onProfileUpdate={handleProfileUpdate}
        />
      )}

      {/* Step 2: Photo Analysis */}
      {currentStep === 2 && !generating && selectedClient && (
        <PhotoAnalysis
          client={selectedClient}
          onAnalysisComplete={handlePhotoAnalysisComplete}
          onSkip={() => setCurrentStep(3)}
        />
      )}

      {/* Step 3: Occasion & Preferences */}
      {currentStep === 3 && !generating && selectedClient && (
        <OccasionPreferences
          preferences={preferences}
          onChange={setPreferences}
          onNext={handleGenerateLooks}
          onBack={() => setCurrentStep(selectedClient.complexion_undertone ? 1 : 2)}
        />
      )}

      {/* Step 4: Recommendations */}
      {currentStep === 4 && !generating && recommendation && (
        <RecommendationsDisplay
          recommendation={recommendation}
          onStartOrder={() => {}}
          onSave={handleSaveConsultation}
          onRegenerate={handleRegenerate}
          onBack={() => setCurrentStep(3)}
        />
      )}
    </div>
  );
}
